// Routes absolute (cross-origin) image URLs through the backend's same-origin
// `/image-proxy` endpoint.
//
// Exists to dodge a browser cache-poisoning bug on the player card page: the
// card renders `<img crossOrigin="anonymous">` (html2canvas needs an
// untainted canvas to export a JPG), but the athlete profile page renders the
// *same* S3 URL as a plain `<img>` with no `crossOrigin`. S3 answers a
// no-Origin request with no `Access-Control-Allow-Origin` and no `Vary`
// header at all, so a browser that hit the profile page first caches that
// CORS-less response under the plain URL. The card page's CORS-mode request
// then reuses the cached entry, fails the CORS check against headers that
// were never there, and the image breaks — but only for a browser that
// already cached the profile page, which is why incognito always "fixes" it.
// Proxying through our own origin sidesteps CORS (and its cache) entirely.

/**
 * @param {string|null|undefined} url an image URL from the API payload —
 *   either an absolute S3/CDN URL or a relative same-origin upload path
 *   (e.g. `/uploads/athletes/photo.jpg`).
 * @param {string} apiUrl this app's `/api` base, e.g. `https://app.prozpects.com/api`.
 * @returns {string|null|undefined} the proxied URL for an absolute input,
 *   the input unchanged for a relative path (already same-origin — and the
 *   proxy would 400 it, since it requires an absolute URL) or a nullish/empty
 *   value (so callers can keep using `{a.photo ? <img/> : placeholder}`).
 */
export function proxiedImageUrl(url, apiUrl) {
  if (!url) return url;
  if (!/^https?:\/\//i.test(url)) return url;
  return `${apiUrl}/image-proxy?url=${encodeURIComponent(url)}`;
}
